import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowUpIcon,
  CaretDownIcon,
  CheckIcon,
  DotsSixVerticalIcon,
  EyeIcon,
  FloppyDiskIcon,
  PlusIcon,
  RocketLaunchIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import {
  type DragEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ApiError, apiRequest } from "../api/client";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { SelectField, TextAreaField, TextField } from "../components/Field";
import { LoadingState } from "../components/LoadingState";
import { StatusBadge } from "../components/StatusBadge";
import { Link, useNavigate, useParams } from "../router";
import type {
  CmsBlock,
  ComponentDefinition,
  ComponentField,
  Locale,
  MediaAsset,
  Page,
  PageDetail,
  PageLocalization,
  PageVersion,
} from "../types";

const FIELD_LABELS: Record<string, string> = {
  alt: "Alt-текст",
  answer: "Ответ",
  body: "Текст",
  caption: "Подпись",
  description: "Описание",
  href: "Ссылка",
  icon: "Иконка",
  image: "Изображение",
  label: "Метка",
  preview: "Превью",
  question: "Вопрос",
  entries: "Текстовые пункты",
  quote: "Цитата",
  role: "Роль",
  tag: "Тег",
  tags: "Теги",
  title: "Заголовок",
};

const LONG_TEXT_KEYS = new Set([
  "answer",
  "body",
  "description",
  "message",
  "quote",
  "text",
]);

function contentSnapshot(blocks: CmsBlock[], seo: Record<string, unknown>) {
  return JSON.stringify({ blocks, seo });
}

function localizationSnapshot(title: string, slug: string) {
  return JSON.stringify({ title: title.trim(), slug: slug.trim() });
}

function fieldLabel(key: string) {
  return FIELD_LABELS[key] ?? key.replaceAll("_", " ");
}

function isImageKey(key: string) {
  return key === "image" || key === "og_image" || key.endsWith("_image");
}

function isLongTextKey(key: string) {
  return LONG_TEXT_KEYS.has(key);
}

function textLengthHelper(value: string, localized?: boolean) {
  const parts = [`${value.length} символов`];
  if (localized) parts.push("только для выбранного языка");
  if (value.length > 220) parts.push("проверьте высоту блока на сайте");
  return parts.join(" · ");
}

function mediaDisplayLabel(asset: MediaAsset, suffix?: string) {
  const size =
    asset.width && asset.height ? `${asset.width}×${asset.height}` : "media";
  return suffix
    ? `${asset.original_name} · ${suffix}`
    : `${asset.original_name} · ${size}`;
}

function collectEditorDiagnostics({
  blocks,
  components,
  media,
  pages,
  seo,
  templateKey,
}: {
  blocks: CmsBlock[];
  components: ComponentDefinition[];
  media: MediaAsset[];
  pages: Page[];
  seo: Record<string, unknown>;
  templateKey: string;
}) {
  const issues: string[] = [];
  const knownMediaUrls = new Set(
    media.flatMap((asset) => [
      asset.url,
      ...asset.variants.map((variant) => variant.url),
    ]),
  );
  const pageIds = new Set(pages.map((page) => page.id));
  const definitions = new Map(components.map((item) => [item.key, item]));

  if (templateKey === "project") {
    const requiredTypes = [
      "project_hero",
      "project_showcase",
      "project_gallery",
      "project_banner",
      "project_text",
      "project_curtain",
    ];
    const blockTypes = new Set(blocks.map((block) => block.type));
    requiredTypes.forEach((type) => {
      if (!blockTypes.has(type)) {
        issues.push(
          `Структура кейса: добавьте «${definitions.get(type)?.label ?? type}»`,
        );
      }
    });
  }

  blocks.forEach((block, blockIndex) => {
    const definition = definitions.get(block.type);
    const prefix = `${String(blockIndex + 1).padStart(2, "0")} · ${definition?.label ?? block.type}`;
    if (!definition) {
      issues.push(`${prefix}: компонент не зарегистрирован`);
      return;
    }
    definition.fields.forEach((field) => {
      const value = block.props[field.name];
      if (field.required) {
        const isEmptyText =
          ["text", "textarea", "rich_text", "image"].includes(field.type) &&
          (typeof value !== "string" || !value.trim());
        const isEmptyList =
          ["list", "relation_list"].includes(field.type) &&
          (!Array.isArray(value) || value.length === 0);
        if (isEmptyText || isEmptyList) {
          issues.push(`${prefix}: пустое поле «${field.label}»`);
        }
      }
      if (field.type === "image" && typeof value === "string" && value) {
        if (!value.toLowerCase().endsWith(".webp")) {
          issues.push(`${prefix}: «${field.label}» должен быть WebP`);
        }
        if (!knownMediaUrls.has(value) && value.startsWith("http")) {
          issues.push(`${prefix}: «${field.label}» ссылается на внешний URL`);
        }
      }
      if (field.type === "relation_list" && Array.isArray(value)) {
        value
          .filter((item): item is string => typeof item === "string")
          .forEach((id) => {
            if (!pageIds.has(id)) {
              issues.push(`${prefix}: связь указывает на удалённую страницу`);
            }
          });
      }
      if (field.type === "list" && Array.isArray(value)) {
        if (field.min_items && value.length < field.min_items) {
          issues.push(
            `${prefix}: «${field.label}» — минимум ${field.min_items} элементов`,
          );
        }
        if (field.max_items && value.length > field.max_items) {
          issues.push(
            `${prefix}: «${field.label}» — максимум ${field.max_items} элементов`,
          );
        }
        value.forEach((item, itemIndex) => {
          if (typeof item !== "object" || item === null) return;
          field.required_item_fields?.forEach((key) => {
            const itemValue = (item as Record<string, unknown>)[key];
            if (typeof itemValue !== "string" || !itemValue.trim()) {
              issues.push(
                `${prefix}: элемент ${itemIndex + 1}, поле «${fieldLabel(key)}» обязательно`,
              );
            }
          });
          Object.entries(item).forEach(([key, itemValue]) => {
            if (isImageKey(key) && typeof itemValue === "string" && itemValue) {
              if (!itemValue.toLowerCase().endsWith(".webp")) {
                issues.push(
                  `${prefix}: элемент ${itemIndex + 1}, поле «${fieldLabel(key)}» должен быть WebP`,
                );
              }
            }
          });
        });
      }
    });
  });

  const ogImage = typeof seo.og_image === "string" ? seo.og_image : "";
  if (ogImage && !ogImage.toLowerCase().endsWith(".webp")) {
    issues.push("SEO: Open Graph image должен быть WebP");
  }

  return issues;
}

function componentPreviewCopy(definition: ComponentDefinition) {
  const fields = definition.fields.map((field) => field.label).slice(0, 5);
  const fieldCopy = fields.length
    ? fields.join(", ")
    : "без редактируемых полей";
  const descriptions: Record<string, string> = {
    contact_hero: "Первый экран контактов с формой и вводным текстом.",
    contact_quote: "Цитатный блок с акцентной фразой.",
    cta: "Финальный призыв к действию с кнопкой.",
    development_stack: "Секция технологического стека.",
    faq: "Аккордеон вопросов и ответов.",
    formats: "Коммерческие форматы работы.",
    hero_home: "Главный экран лендинга: заголовок, описание и CTA.",
    manifesto: "Короткий смысловой блок о подходе студии.",
    process: "Этапы процесса и порядок работы.",
    project_gallery:
      "Адаптивная фотогалерея кейса: достаточно одного изображения, оптимально — от одного до трёх.",
    project_hero: "Первый экран страницы кейса.",
    project_showcase:
      "Три слайда слева и синхронный текстовый сценарий справа.",
    project_curtain:
      "Большое утверждение с анимированной матрицей изображений под ним.",
    project_banner: "Один широкий кадр без скрытых связанных секций.",
    project_duo_image:
      "Две независимые большие медиа-колонки: отдельный выбор изображения и alt для каждой.",
    project_text: "Структурированный текстовый раздел кейса с тезисами.",
    reviews: "Отзывы клиентов.",
    services_bento: "Сетка услуг / направлений.",
    text_block: "Текстовый контент с абзацами или списком тезисов.",
    works: "Секция кейсов и связанных проектных страниц.",
  };
  return {
    description:
      descriptions[definition.key] ??
      `Компонент «${definition.label}» из группы «${definition.group}».`,
    fields: fieldCopy,
  };
}

export function PageEditorPage() {
  const { pageId = "" } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PageDetail | null>(null);
  const [locales, setLocales] = useState<Locale[]>([]);
  const [components, setComponents] = useState<ComponentDefinition[]>([]);
  const [media, setMedia] = useState<MediaAsset[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [versions, setVersions] = useState<PageVersion[]>([]);
  const [blocks, setBlocks] = useState<CmsBlock[]>([]);
  const [seo, setSeo] = useState<Record<string, unknown>>({});
  const [loadedVersionId, setLoadedVersionId] = useState("");
  const [loadedSnapshot, setLoadedSnapshot] = useState("");
  const [localizationTitle, setLocalizationTitle] = useState("");
  const [localizationSlug, setLocalizationSlug] = useState("");
  const [loadedLocalizationSnapshot, setLoadedLocalizationSnapshot] =
    useState("");
  const [changeNote, setChangeNote] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [expandedBlockIds, setExpandedBlockIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [activeEditorTab, setActiveEditorTab] = useState<"structure" | "seo">(
    "structure",
  );
  const [draggingBlockId, setDraggingBlockId] = useState("");
  const [dragOverBlockId, setDragOverBlockId] = useState("");

  const loadDetail = useCallback(async () => {
    const result = await apiRequest<PageDetail>(
      `/api/v1/admin/pages/${pageId}`,
    );
    setDetail(result);
    setSelectedId((current) => current || result.localizations[0]?.id || "");
  }, [pageId]);

  useEffect(() => {
    Promise.all([
      loadDetail(),
      apiRequest<Locale[]>("/api/v1/admin/locales").then(setLocales),
      apiRequest<ComponentDefinition[]>("/api/v1/admin/components").then(
        setComponents,
      ),
      apiRequest<MediaAsset[]>("/api/v1/admin/media").then(setMedia),
      apiRequest<Page[]>("/api/v1/admin/pages").then(setPages),
    ]).catch(() => setError("Не удалось открыть редактор страницы"));
  }, [loadDetail]);

  const selected =
    detail?.localizations.find((item) => item.id === selectedId) ?? null;
  const selectedLocalizationId = selected?.id;
  const selectedDraftVersionId = selected?.draft_version_id;

  useEffect(() => {
    const title = selected?.title ?? "";
    const slug = selected?.slug ?? "";
    setLocalizationTitle(title);
    setLocalizationSlug(slug);
    setLoadedLocalizationSnapshot(localizationSnapshot(title, slug));
  }, [selected?.id, selected?.slug, selected?.title]);

  useEffect(() => {
    if (!selectedLocalizationId) {
      setVersions([]);
      setBlocks([]);
      setSeo({});
      setExpandedBlockIds(new Set());
      return;
    }
    apiRequest<PageVersion[]>(
      `/api/v1/admin/pages/${pageId}/localizations/${selectedLocalizationId}/versions`,
    )
      .then((items) => {
        setVersions(items);
        const draft =
          items.find((item) => item.id === selectedDraftVersionId) ?? items[0];
        setBlocks(draft?.content_json.blocks ?? []);
        setSeo(draft?.seo_json ?? {});
        setExpandedBlockIds(new Set());
        setLoadedVersionId(draft?.id ?? "");
        setLoadedSnapshot(
          contentSnapshot(
            draft?.content_json.blocks ?? [],
            draft?.seo_json ?? {},
          ),
        );
      })
      .catch(() => setError("Не удалось загрузить версии страницы"));
  }, [pageId, selectedDraftVersionId, selectedLocalizationId]);

  const currentSnapshot = useMemo(
    () => contentSnapshot(blocks, seo),
    [blocks, seo],
  );
  const hasLocalChanges = Boolean(
    loadedSnapshot && currentSnapshot !== loadedSnapshot,
  );
  const currentLocalizationSnapshot = localizationSnapshot(
    localizationTitle,
    localizationSlug,
  );
  const hasLocalizationChanges = Boolean(
    selected &&
    loadedLocalizationSnapshot &&
    currentLocalizationSnapshot !== loadedLocalizationSnapshot,
  );
  const hasUnsavedChanges = hasLocalChanges || hasLocalizationChanges;
  const diagnostics = useMemo(
    () =>
      collectEditorDiagnostics({
        blocks,
        components,
        media,
        pages,
        seo,
        templateKey: detail?.page.template_key ?? "",
      }),
    [blocks, components, detail?.page.template_key, media, pages, seo],
  );

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  const groupedComponents = useMemo(() => {
    const groups = new Map<string, ComponentDefinition[]>();
    for (const component of components.filter((item) => {
      if (!detail?.page.template_key) return true;
      return (
        item.locked_to_templates.length === 0 ||
        item.locked_to_templates.includes(detail.page.template_key)
      );
    })) {
      const list = groups.get(component.group) ?? [];
      list.push(component);
      groups.set(component.group, list);
    }
    return groups;
  }, [components, detail?.page.template_key]);

  function addBlock(definition: ComponentDefinition) {
    const blockId = crypto.randomUUID();
    const next = [
      ...blocks,
      {
        id: blockId,
        type: definition.key,
        schema_version: definition.schema_version,
        props: structuredClone(definition.default_props),
      },
    ];
    setBlocks(next);
    setExpandedBlockIds(new Set([blockId]));
    setActiveEditorTab("structure");
    setNotice("");
  }

  function toggleBlock(blockId: string) {
    setExpandedBlockIds((current) => {
      const next = new Set(current);
      if (next.has(blockId)) {
        next.delete(blockId);
      } else {
        next.add(blockId);
      }
      return next;
    });
  }

  function reorderBlock(sourceId: string, targetId: string) {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setBlocks((items) => {
      const sourceIndex = items.findIndex((item) => item.id === sourceId);
      const targetIndex = items.findIndex((item) => item.id === targetId);
      if (sourceIndex < 0 || targetIndex < 0) return items;
      const next = [...items];
      const [moved] = next.splice(sourceIndex, 1);
      if (!moved) return items;
      next.splice(targetIndex, 0, moved);
      return next;
    });
  }

  function clearDragState() {
    setDraggingBlockId("");
    setDragOverBlockId("");
  }

  function loadVersion(version: PageVersion) {
    setBlocks(version.content_json.blocks ?? []);
    setSeo(version.seo_json ?? {});
    setExpandedBlockIds(new Set());
    setLoadedVersionId(version.id);
    setLoadedSnapshot(
      contentSnapshot(
        version.content_json.blocks ?? [],
        version.seo_json ?? {},
      ),
    );
    setChangeNote(`Откат к версии ${version.version}`);
    setNotice(
      `Версия ${version.version} загружена в редактор. Нажмите «Сохранить», чтобы сделать её новым черновиком.`,
    );
  }

  async function previewVersion(version: PageVersion) {
    if (!selected) return;
    setError("");
    try {
      const result = await apiRequest<{ url: string }>(
        `/api/v1/admin/pages/${pageId}/localizations/${selected.id}/versions/${version.id}/preview-url`,
        { method: "POST" },
      );
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось открыть preview версии",
      );
    }
  }

  async function restoreVersion(version: PageVersion) {
    if (!selected) return;
    if (
      !window.confirm(`Восстановить v${version.version} как новый черновик?`)
    ) {
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const restored = await apiRequest<PageVersion>(
        `/api/v1/admin/pages/${pageId}/localizations/${selected.id}/versions/${version.id}/restore`,
        { method: "POST" },
      );
      setBlocks(restored.content_json.blocks ?? []);
      setSeo(restored.seo_json ?? {});
      setExpandedBlockIds(new Set());
      setLoadedVersionId(restored.id);
      setLoadedSnapshot(
        contentSnapshot(
          restored.content_json.blocks ?? [],
          restored.seo_json ?? {},
        ),
      );
      setChangeNote("");
      await loadDetail();
      setNotice(
        `Версия ${version.version} восстановлена как v${restored.version}`,
      );
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось восстановить версию",
      );
    } finally {
      setSaving(false);
    }
  }

  async function saveDraft() {
    if (!selected) return;
    const title = localizationTitle.trim();
    const slug = localizationSlug.trim();
    if (!title || !slug) {
      setError("Заполните название страницы и slug");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const savedParts: string[] = [];

      if (hasLocalizationChanges) {
        const updated = await apiRequest<PageLocalization>(
          `/api/v1/admin/pages/${pageId}/localizations/${selected.id}`,
          {
            method: "PATCH",
            body: JSON.stringify({ title, slug }),
          },
        );
        setLocalizationTitle(updated.title);
        setLocalizationSlug(updated.slug);
        setLoadedLocalizationSnapshot(
          localizationSnapshot(updated.title, updated.slug),
        );
        savedParts.push("название и URL");
      }

      if (hasLocalChanges || !selected.draft_version_id) {
        const saved = await apiRequest<PageVersion>(
          `/api/v1/admin/pages/${pageId}/localizations/${selected.id}/versions`,
          {
            method: "POST",
            body: JSON.stringify({
              content: { blocks },
              seo,
              change_note: changeNote || null,
              expected_draft_version_id: selected.draft_version_id,
            }),
          },
        );
        setLoadedSnapshot(
          contentSnapshot(
            saved.content_json.blocks ?? [],
            saved.seo_json ?? {},
          ),
        );
        setLoadedVersionId(saved.id);
        savedParts.push(`черновик v${saved.version}`);
      }

      await loadDetail();
      setChangeNote("");
      setNotice(
        savedParts.length
          ? `Сохранено: ${savedParts.join(", ")}`
          : "Изменений для сохранения нет",
      );
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось сохранить черновик",
      );
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (!selected) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await apiRequest(
        `/api/v1/admin/pages/${pageId}/localizations/${selected.id}/publish`,
        { method: "POST" },
      );
      await loadDetail();
      setNotice("Локализованная версия опубликована и отправлена в Next.js");
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось опубликовать страницу",
      );
    } finally {
      setSaving(false);
    }
  }

  async function unpublish() {
    if (!selected) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await apiRequest(
        `/api/v1/admin/pages/${pageId}/localizations/${selected.id}/unpublish`,
        { method: "POST" },
      );
      await loadDetail();
      setNotice(
        "Публикация снята. Страница больше не отдаётся публично для этой локали.",
      );
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось снять публикацию",
      );
    } finally {
      setSaving(false);
    }
  }

  async function archivePage() {
    if (
      !window.confirm(`Архивировать страницу «${detail?.page.internal_name}»?`)
    ) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await apiRequest(`/api/v1/admin/pages/${pageId}`, { method: "DELETE" });
      navigate("/pages");
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось архивировать страницу",
      );
    } finally {
      setSaving(false);
    }
  }

  async function duplicatePage() {
    const pageKey = window.prompt(
      "Новый ключ страницы, например services-copy",
    );
    if (!pageKey) return;
    const internalName = window.prompt(
      "Внутреннее имя копии",
      `${detail?.page.internal_name} copy`,
    );
    if (!internalName) return;
    setSaving(true);
    setError("");
    try {
      const copy = await apiRequest<Page>(
        `/api/v1/admin/pages/${pageId}/duplicate`,
        {
          method: "POST",
          body: JSON.stringify({
            page_key: pageKey,
            internal_name: internalName,
          }),
        },
      );
      navigate(`/pages/${copy.id}`);
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось создать копию страницы",
      );
    } finally {
      setSaving(false);
    }
  }

  const hasUnpublishedChanges =
    selected?.draft_version_id &&
    selected.draft_version_id !== selected.published_version_id;

  async function preview() {
    if (!selected) return;
    setError("");
    try {
      const result = await apiRequest<{ url: string }>(
        `/api/v1/admin/pages/${pageId}/localizations/${selected.id}/preview-url`,
        { method: "POST" },
      );
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось открыть preview",
      );
    }
  }

  if (!detail)
    return (
      <div className="page">
        <LoadingState rows={7} />
      </div>
    );

  return (
    <div className="editor-page">
      <header className="editor-topbar">
        <div>
          <Link className="back-link" to="/pages">
            <ArrowLeftIcon size={16} /> Страницы
          </Link>
          <h1>{detail.page.internal_name}</h1>
          <span className="mono">
            {detail.page.page_key} / {detail.page.template_key}
          </span>
        </div>
        <div className="editor-topbar__actions">
          {selected && (
            <StatusBadge
              status={
                hasUnsavedChanges
                  ? "draft"
                  : hasUnpublishedChanges
                    ? "draft"
                    : selected.translation_status === "published"
                      ? "published"
                      : "draft"
              }
            >
              {hasUnsavedChanges
                ? "Несохранено"
                : hasUnpublishedChanges
                  ? "Есть изменения"
                  : selected.translation_status === "published"
                    ? "Опубликовано"
                    : "Черновик"}
            </StatusBadge>
          )}
          <Button
            disabled={!selected || saving}
            onClick={() => void saveDraft()}
            tone="secondary"
          >
            <FloppyDiskIcon size={17} /> Сохранить
          </Button>
          <Button
            disabled={!selected?.draft_version_id || saving}
            onClick={() => void preview()}
            tone="secondary"
          >
            <EyeIcon size={17} /> Preview
          </Button>
          <Button
            disabled={
              !selected?.draft_version_id ||
              saving ||
              diagnostics.length > 0 ||
              hasLocalizationChanges
            }
            onClick={() => void publish()}
            title={
              hasLocalizationChanges
                ? "Сначала сохраните название и URL"
                : diagnostics.length
                  ? "Исправьте ошибки контента перед публикацией"
                  : undefined
            }
          >
            <RocketLaunchIcon size={17} /> Опубликовать
          </Button>
          <Button
            disabled={!selected?.published_version_id || saving}
            onClick={() => void unpublish()}
            tone="secondary"
          >
            Снять публикацию
          </Button>
          <Button
            disabled={saving}
            onClick={() => void duplicatePage()}
            tone="secondary"
          >
            <PlusIcon size={17} /> Дублировать
          </Button>
          <Button
            disabled={saving}
            onClick={() => void archivePage()}
            tone="quiet"
          >
            <TrashIcon size={17} /> Архив
          </Button>
        </div>
      </header>

      {error && (
        <div className="notice notice--error editor-notice">{error}</div>
      )}
      {notice && (
        <div className="notice notice--success editor-notice">
          <CheckIcon size={17} /> {notice}
        </div>
      )}
      {diagnostics.length ? (
        <div className="editor-diagnostics">
          <strong>Проверка контента</strong>
          <ul>
            {diagnostics.slice(0, 8).map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
          {diagnostics.length > 8 ? (
            <small>Ещё {diagnostics.length - 8} предупреждений</small>
          ) : null}
        </div>
      ) : null}

      <div className="editor-layout">
        <aside className="editor-languages">
          <span className="eyebrow">Языковые версии</span>
          <div className="language-tabs">
            {detail.localizations.map((item) => (
              <button
                className={item.id === selectedId ? "is-active" : ""}
                key={item.id}
                onClick={() => setSelectedId(item.id)}
              >
                <i>{item.locale.code.slice(0, 2).toUpperCase()}</i>
                <span>
                  <strong>{item.locale.label}</strong>
                  <small>/{item.slug}</small>
                </span>
              </button>
            ))}
          </div>
          {selected ? (
            <form
              className="localization-settings"
              onSubmit={(event) => {
                event.preventDefault();
                void saveDraft();
              }}
            >
              <div className="localization-settings__heading">
                <span className="eyebrow">Карточка и URL</span>
                <small>
                  Эти данные используются на сайте, отдельно от SEO Title.
                </small>
              </div>
              <TextField
                helper={
                  detail.page.template_key === "project"
                    ? "Показывается в карточках кейсов и навигации."
                    : "Показывается в навигации и списках страниц."
                }
                label={
                  detail.page.template_key === "project"
                    ? "Название кейса"
                    : "Название страницы"
                }
                maxLength={255}
                onChange={(event) => setLocalizationTitle(event.target.value)}
                required
                value={localizationTitle}
              />
              <TextField
                helper="Адрес языковой версии страницы."
                label="Slug"
                maxLength={255}
                onChange={(event) => setLocalizationSlug(event.target.value)}
                required
                value={localizationSlug}
              />
              <Button
                disabled={!hasLocalizationChanges || saving}
                tone="secondary"
                type="submit"
              >
                <FloppyDiskIcon size={15} /> Сохранить данные
              </Button>
            </form>
          ) : null}
          <CreateLocalization
            available={locales.filter(
              (locale) =>
                !detail.localizations.some(
                  (item) => item.locale.id === locale.id,
                ),
            )}
            onCreated={loadDetail}
            pageId={pageId}
          />
          <div className="version-summary">
            <span>Версии</span>
            <strong>{versions.length}</strong>
            <small>
              Каждое сохранение создаёт неизменяемый снимок. Версию можно
              открыть в preview или восстановить как новый черновик.
            </small>
            <div className="version-list">
              {versions.slice(0, 8).map((version) => (
                <div
                  className={version.id === loadedVersionId ? "is-active" : ""}
                  key={version.id}
                >
                  <button onClick={() => loadVersion(version)} type="button">
                    <span>v{version.version}</span>
                    <small>
                      {version.id === selected?.published_version_id
                        ? "published"
                        : version.id === selected?.draft_version_id
                          ? "draft"
                          : new Date(version.created_at).toLocaleDateString()}
                    </small>
                  </button>
                  <div className="version-actions">
                    <button
                      aria-label={`Preview v${version.version}`}
                      onClick={() => void previewVersion(version)}
                      title="Preview"
                      type="button"
                    >
                      <EyeIcon size={13} />
                    </button>
                    <button
                      aria-label={`Restore v${version.version}`}
                      disabled={saving}
                      onClick={() => void restoreVersion(version)}
                      title="Восстановить"
                      type="button"
                    >
                      <ArrowUpIcon size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <section className="editor-canvas">
          {!selected ? (
            <EmptyState
              title="Добавьте языковую версию"
              description="Контент и SEO управляются отдельно для каждого языка."
            />
          ) : (
            <>
              <div
                className="editor-tabs"
                role="tablist"
                aria-label="Редактор страницы"
              >
                <button
                  aria-selected={activeEditorTab === "structure"}
                  className={activeEditorTab === "structure" ? "is-active" : ""}
                  onClick={() => setActiveEditorTab("structure")}
                  role="tab"
                  type="button"
                >
                  Структура
                  <span>{blocks.length} blocks</span>
                </button>
                <button
                  aria-selected={activeEditorTab === "seo"}
                  className={activeEditorTab === "seo" ? "is-active" : ""}
                  onClick={() => setActiveEditorTab("seo")}
                  role="tab"
                  type="button"
                >
                  SEO / Meta
                  <span>
                    {String(seo.title ?? "") ? "настроено" : "черновик"}
                  </span>
                </button>
              </div>

              {activeEditorTab === "structure" ? (
                <section className="editor-section">
                  <div className="section-heading">
                    <div>
                      <span className="eyebrow">Структура</span>
                      <h2>Компоненты страницы</h2>
                    </div>
                    <span className="mono">{blocks.length} blocks</span>
                  </div>
                  <div className="block-list">
                    {blocks.map((block, index) => (
                      <BlockEditor
                        block={block}
                        definition={components.find(
                          (item) => item.key === block.type,
                        )}
                        dragOver={dragOverBlockId === block.id}
                        dragging={draggingBlockId === block.id}
                        expanded={expandedBlockIds.has(block.id)}
                        index={index}
                        key={block.id}
                        media={media}
                        pages={pages}
                        onChange={(next) =>
                          setBlocks((items) =>
                            items.map((item) =>
                              item.id === block.id ? next : item,
                            ),
                          )
                        }
                        onDragEnd={clearDragState}
                        onDragOver={(event) => {
                          if (!draggingBlockId || draggingBlockId === block.id)
                            return;
                          event.preventDefault();
                          setDragOverBlockId(block.id);
                        }}
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", block.id);
                          setDraggingBlockId(block.id);
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          const sourceId =
                            event.dataTransfer.getData("text/plain") ||
                            draggingBlockId;
                          reorderBlock(sourceId, block.id);
                          clearDragState();
                        }}
                        onRemove={() =>
                          setBlocks((items) =>
                            items.filter((item) => item.id !== block.id),
                          )
                        }
                        onToggle={() => toggleBlock(block.id)}
                      />
                    ))}
                    {blocks.length === 0 && (
                      <EmptyState
                        title="Страница пока пустая"
                        description="Добавьте первый зарегистрированный компонент из каталога справа."
                      />
                    )}
                  </div>
                </section>
              ) : (
                <section className="editor-section seo-editor seo-editor--tab">
                  <div className="section-heading">
                    <div>
                      <span className="eyebrow">SEO / Meta</span>
                      <h2>Настройки локали</h2>
                      <p>
                        Title, description, canonical URL и Open Graph отдельно
                        для выбранного языка.
                      </p>
                    </div>
                    <span className="webp-label">OG: WebP only</span>
                  </div>
                  <div className="form-grid">
                    <TextField
                      label="Title"
                      onChange={(e) =>
                        setSeo({ ...seo, title: e.target.value })
                      }
                      value={String(seo.title ?? "")}
                    />
                    <TextField
                      label="Canonical URL"
                      onChange={(e) =>
                        setSeo({ ...seo, canonical_url: e.target.value })
                      }
                      value={String(seo.canonical_url ?? "")}
                    />
                    <TextAreaField
                      label="Description"
                      onChange={(e) =>
                        setSeo({ ...seo, description: e.target.value })
                      }
                      rows={4}
                      value={String(seo.description ?? "")}
                    />
                    <OgImagePicker
                      media={media}
                      onChange={(value) => setSeo({ ...seo, og_image: value })}
                      value={String(seo.og_image ?? "")}
                    />
                  </div>
                </section>
              )}
              <TextField
                helper="Будет сохранено в истории версии"
                label="Комментарий к изменению"
                onChange={(e) => setChangeNote(e.target.value)}
                placeholder="Что изменилось в этой версии"
                value={changeNote}
              />
            </>
          )}
        </section>

        <aside className="component-palette">
          <span className="eyebrow">Каталог блоков</span>
          <h2>Добавить компонент</h2>
          <p>
            Схемы зарегистрированы backend-кодом и проходят серверную валидацию.
          </p>
          {[...groupedComponents].map(([group, items]) => (
            <div className="palette-group" key={group}>
              <strong>{group}</strong>
              {items.map((item) => (
                <button
                  key={item.key}
                  onClick={() => addBlock(item)}
                  type="button"
                >
                  <PlusIcon size={15} />
                  <span>
                    {item.label}
                    <small>
                      {item.key} · v{item.schema_version}
                    </small>
                  </span>
                  <ComponentPreview definition={item} />
                </button>
              ))}
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}

function BlockEditor({
  block,
  definition,
  dragging,
  dragOver,
  expanded,
  index,
  media,
  pages,
  onChange,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onRemove,
  onToggle,
}: {
  block: CmsBlock;
  definition?: ComponentDefinition;
  dragging: boolean;
  dragOver: boolean;
  expanded: boolean;
  index: number;
  media: MediaAsset[];
  pages: Page[];
  onChange: (block: CmsBlock) => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDragStart: (event: DragEvent<HTMLButtonElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
  onRemove: () => void;
  onToggle: () => void;
}) {
  return (
    <article
      className={`block-editor ${expanded ? "is-open" : "is-collapsed"} ${
        dragging ? "is-dragging" : ""
      } ${dragOver ? "is-drag-over" : ""}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <header>
        <button
          className="block-drag-handle"
          draggable
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          title="Перетащить блок"
          type="button"
        >
          <DotsSixVerticalIcon size={17} />
        </button>
        <button
          aria-expanded={expanded}
          className="block-toggle"
          onClick={onToggle}
          type="button"
        >
          <span className="block-index">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span>
            <strong>{definition?.label ?? block.type}</strong>
            <small>
              {block.type} · schema v{block.schema_version}
            </small>
          </span>
          <CaretDownIcon size={15} />
        </button>
        <div className="block-actions">
          <button onClick={onRemove} title="Удалить" type="button">
            <TrashIcon size={15} />
          </button>
        </div>
      </header>
      {expanded ? (
        <div className="schema-fields">
          {definition?.fields.map((field) => (
            <SchemaField
              field={field}
              key={field.name}
              media={media}
              pages={pages}
              onChange={(value) =>
                onChange({
                  ...block,
                  props: { ...block.props, [field.name]: value },
                })
              }
              value={block.props[field.name]}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
}

function ComponentPreview({ definition }: { definition: ComponentDefinition }) {
  const preview = componentPreviewCopy(definition);
  return (
    <div className="palette-preview" aria-hidden="true">
      <div className={`section-thumb section-thumb--${definition.key}`}>
        <span className="section-thumb__a" />
        <span className="section-thumb__b" />
        <span className="section-thumb__c" />
        <span className="section-thumb__d" />
      </div>
      <strong>{definition.label}</strong>
      <p>{preview.description}</p>
      <small>Поля: {preview.fields}</small>
    </div>
  );
}

function OgImagePicker({
  media,
  value,
  onChange,
}: {
  media: MediaAsset[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const options = media.flatMap((asset) =>
    asset.variants
      .filter((variant) => variant.variant_key === "social_1200x630")
      .map((variant) => ({ asset, variant })),
  );
  const filteredOptions = options.filter(({ asset }) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return true;
    return [asset.original_name, asset.alt_text]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalized);
  });
  const selected = options.find((item) => item.variant.url === value);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);
  return (
    <fieldset className="og-picker">
      <legend>Open Graph image</legend>
      <p>Для соцсетей используется автоматически созданный WebP 1200×630.</p>
      {selected ? (
        <div className="og-picker__selected">
          <img alt={selected.asset.alt_text || ""} src={selected.variant.url} />
          <span>
            <strong>{selected.asset.original_name}</strong>
            <small>
              {selected.variant.width}×{selected.variant.height} · WebP
            </small>
          </span>
          <button onClick={() => setOpen(true)} type="button">
            Заменить
          </button>
        </div>
      ) : (
        <button
          className="og-picker__empty-card"
          onClick={() => setOpen(true)}
          type="button"
        >
          <span>OG image не выбран</span>
          <strong>Выбрать изображение</strong>
          <small>Откроется медиапикер WebP 1200×630</small>
        </button>
      )}
      {!options.length ? (
        <Link className="media-field__empty" to="/media">
          Загрузить изображение в медиатеку
        </Link>
      ) : null}
      {value ? (
        <Button onClick={() => onChange("")} tone="quiet" type="button">
          Сбросить OG image
        </Button>
      ) : null}
      {open ? (
        <div
          className="media-picker-modal"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
          role="presentation"
        >
          <section
            aria-modal="true"
            className="media-picker-dialog"
            role="dialog"
          >
            <header>
              <div>
                <span className="eyebrow">Медиапикер</span>
                <h2>Open Graph image</h2>
                <p>Выберите WebP-вариант 1200×630 для соцсетей.</p>
              </div>
              <button onClick={() => setOpen(false)} type="button">
                Закрыть
              </button>
            </header>
            <div className="media-picker-toolbar">
              <input
                autoFocus
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Поиск по имени файла или alt"
                value={query}
              />
              <span>{filteredOptions.length} файлов</span>
            </div>
            <div className="media-picker-grid">
              <button
                className={!value ? "is-selected" : ""}
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
                type="button"
              >
                <span className="og-picker__empty">Не выбрано</span>
                <strong>Без OG image</strong>
                <small>Использовать дефолт сайта</small>
              </button>
              {filteredOptions.map(({ asset, variant }) => (
                <button
                  className={variant.url === value ? "is-selected" : ""}
                  key={variant.id}
                  onClick={() => {
                    onChange(variant.url);
                    setOpen(false);
                  }}
                  type="button"
                >
                  <img alt={asset.alt_text || ""} src={variant.url} />
                  <strong>{asset.original_name}</strong>
                  <small>
                    {variant.width}×{variant.height} · {variant.variant_key}
                  </small>
                </button>
              ))}
              {!filteredOptions.length ? (
                <div className="inline-empty">Поиск ничего не нашёл.</div>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </fieldset>
  );
}

function SchemaField({
  field,
  value,
  onChange,
  media,
  pages,
}: {
  field: ComponentField;
  value: unknown;
  onChange: (value: unknown) => void;
  media: MediaAsset[];
  pages: Page[];
}) {
  const helper = field.localized
    ? "Поле относится только к выбранному языку"
    : undefined;
  if (field.type === "textarea" || field.type === "rich_text") {
    const stringValue = typeof value === "string" ? value : "";
    return (
      <TextAreaField
        helper={textLengthHelper(stringValue, field.localized)}
        label={field.label}
        onChange={(event) => onChange(event.target.value)}
        rows={field.type === "rich_text" ? 7 : 4}
        value={stringValue}
      />
    );
  }
  if (field.type === "image") {
    return (
      <MediaField
        label={field.label}
        media={media}
        onChange={onChange}
        value={typeof value === "string" ? value : ""}
      />
    );
  }
  if (field.type === "link") {
    const link =
      typeof value === "object" && value !== null
        ? (value as { label?: string; href?: string })
        : {};
    return (
      <fieldset className="schema-link-field">
        <legend>{field.label}</legend>
        <TextField
          label="Текст"
          onChange={(event) => onChange({ ...link, label: event.target.value })}
          value={link.label ?? ""}
        />
        <TextField
          label="Ссылка"
          onChange={(event) => onChange({ ...link, href: event.target.value })}
          placeholder="/contact"
          value={link.href ?? ""}
        />
      </fieldset>
    );
  }
  if (field.type === "list" || field.type === "relation_list") {
    if (field.type === "relation_list") {
      return (
        <PageRelationField
          label={field.label}
          onChange={onChange}
          pages={pages}
          value={value}
        />
      );
    }
    return (
      <VisualListField
        field={field}
        media={media}
        onChange={onChange}
        value={value}
      />
    );
  }
  return (
    <TextField
      helper={
        typeof value === "string"
          ? textLengthHelper(value, field.localized)
          : helper
      }
      label={field.label}
      onChange={(event) => onChange(event.target.value)}
      value={typeof value === "string" ? value : ""}
    />
  );
}

function PageRelationField({
  label,
  pages,
  value,
  onChange,
}: {
  label: string;
  pages: Page[];
  value: unknown;
  onChange: (value: string[]) => void;
}) {
  const selected = Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
  const projectPages = pages.filter((page) => page.template_key === "project");
  const selectedPages = selected
    .map((id) => projectPages.find((page) => page.id === id))
    .filter((page): page is Page => Boolean(page));
  const availablePages = projectPages.filter(
    (page) => !selected.includes(page.id),
  );
  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= selected.length) return;
    const next = [...selected];
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next);
  };
  return (
    <fieldset className="visual-list-field">
      <legend>{label}</legend>
      <small>
        Порядок здесь — это порядок вывода на сайте. Связь хранится по
        стабильному ID страницы.
      </small>
      {selectedPages.length ? (
        <div className="relation-stack">
          {selectedPages.map((page, index) => (
            <div className="relation-option relation-option--row" key={page.id}>
              <span>
                <strong>{page.internal_name}</strong>
                <small>{page.page_key}</small>
              </span>
              <div className="mini-actions">
                <button
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  type="button"
                >
                  <ArrowUpIcon size={14} />
                </button>
                <button
                  disabled={index === selectedPages.length - 1}
                  onClick={() => move(index, 1)}
                  type="button"
                >
                  <ArrowDownIcon size={14} />
                </button>
                <button
                  onClick={() =>
                    onChange(selected.filter((id) => id !== page.id))
                  }
                  type="button"
                >
                  <TrashIcon size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <span className="field__helper">Связанные кейсы пока не выбраны.</span>
      )}
      {availablePages.length ? (
        <SelectField
          label="Добавить кейс"
          onChange={(event) => {
            if (!event.target.value) return;
            onChange([...selected, event.target.value]);
            event.target.value = "";
          }}
          value=""
        >
          <option value="">Выберите страницу</option>
          {availablePages.map((page) => (
            <option key={page.id} value={page.id}>
              {page.internal_name} · {page.page_key}
            </option>
          ))}
        </SelectField>
      ) : null}
      {!projectPages.length ? (
        <span className="field__helper">
          Создайте страницу с шаблоном «Кейс», чтобы связать её с блоком.
        </span>
      ) : null}
    </fieldset>
  );
}

function MediaField({
  label,
  media,
  value,
  onChange,
}: {
  label: string;
  media: MediaAsset[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = media.find(
    (asset) =>
      asset.url === value ||
      asset.variants.some((variant) => variant.url === value),
  );
  const selectedVariant = selected?.variants.find(
    (variant) => variant.url === value,
  );
  const selectedUrl = selectedVariant?.url ?? selected?.url;
  return (
    <div className="media-field">
      <span className="media-field__label">{label}</span>
      {selected ? (
        <button
          className="media-field__selected"
          onClick={() => setOpen(true)}
          type="button"
        >
          <img alt={selected.alt_text || ""} src={selectedUrl} />
          <span>
            <strong>
              {mediaDisplayLabel(selected, selectedVariant?.variant_key)}
            </strong>
            <small>{selected.alt_text || "Без alt-текста"}</small>
          </span>
          <em>Заменить</em>
        </button>
      ) : value ? (
        <button
          className="media-field__selected media-field__selected--external"
          onClick={() => setOpen(true)}
          type="button"
        >
          <span>
            <strong>Внешний URL</strong>
            <small>{value}</small>
          </span>
          <em>Заменить</em>
        </button>
      ) : (
        <button
          className="media-field__empty"
          disabled={!media.length}
          onClick={() => setOpen(true)}
          type="button"
        >
          {media.length
            ? "Выбрать изображение из медиатеки"
            : "Медиатека пуста"}
        </button>
      )}
      {media.length ? (
        <button
          className="media-field__clear"
          disabled={!value}
          onClick={() => onChange("")}
          type="button"
        >
          Очистить
        </button>
      ) : (
        <Link className="media-field__library-link" to="/media">
          Загрузить в медиатеку
        </Link>
      )}
      {open ? (
        <MediaPickerDialog
          label={label}
          media={media}
          onChange={onChange}
          onClose={() => setOpen(false)}
          value={value}
        />
      ) : null}
    </div>
  );
}

function MediaPickerDialog({
  label,
  media,
  value,
  onChange,
  onClose,
}: {
  label: string;
  media: MediaAsset[];
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const visibleMedia = media.filter((asset) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return true;
    return [asset.original_name, asset.alt_text]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalized);
  });
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);
  return (
    <div
      className="media-picker-modal"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <section aria-modal="true" className="media-picker-dialog" role="dialog">
        <header>
          <div>
            <span className="eyebrow">Медиапикер</span>
            <h2>{label}</h2>
            <p>
              Выберите WebP-изображение. Изменение сразу попадёт в превью блока.
            </p>
          </div>
          <button onClick={onClose} type="button">
            Закрыть
          </button>
        </header>
        <div className="media-picker-toolbar">
          <input
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Поиск по имени файла или alt"
            value={query}
          />
          <span>{visibleMedia.length} файлов</span>
        </div>
        <div className="media-picker-grid">
          <button
            className={!value ? "is-selected" : ""}
            onClick={() => {
              onChange("");
              onClose();
            }}
            type="button"
          >
            <span className="og-picker__empty">Не выбрано</span>
            <strong>Без изображения</strong>
            <small>Очистить поле</small>
          </button>
          {visibleMedia.map((asset) => (
            <button
              className={asset.url === value ? "is-selected" : ""}
              key={asset.id}
              onClick={() => {
                onChange(asset.url);
                onClose();
              }}
              type="button"
            >
              <img alt={asset.alt_text || ""} src={asset.url} />
              <strong>{asset.original_name}</strong>
              <small>
                {asset.width}×{asset.height} · WebP
                {asset.alt_text ? ` · ${asset.alt_text}` : ""}
              </small>
            </button>
          ))}
          {!visibleMedia.length ? (
            <div className="inline-empty">Поиск ничего не нашёл.</div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function VisualListField({
  field,
  media,
  value,
  onChange,
}: {
  field: ComponentField;
  media: MediaAsset[];
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const items = Array.isArray(value) ? value : [];
  const objectItems = items.map((item) =>
    typeof item === "object" && item !== null
      ? (item as Record<string, unknown>)
      : { value: String(item) },
  );
  const keys = field.item_fields?.length
    ? field.item_fields
    : [...new Set(objectItems.flatMap((item) => Object.keys(item)))];
  const emptyItem = Object.fromEntries(
    (keys.length ? keys : ["title", "description"]).map((key) => [key, ""]),
  );
  const imageKey = keys.find(isImageKey);
  const update = (index: number, key: string, next: string) => {
    onChange(
      objectItems.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: next } : item,
      ),
    );
  };
  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= objectItems.length) return;
    const next = [...objectItems];
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next);
  };
  return (
    <fieldset className="visual-list-field">
      <legend>{field.label}</legend>
      <small>
        Добавляйте и редактируйте карточки без JSON. Порядок элементов
        сохраняется на сайте.
      </small>
      {imageKey ? (
        <div className="visual-list-field__gallery-preview">
          <header>
            <strong>Превью галереи</strong>
            <span>{objectItems.length} изображений</span>
          </header>
          <div>
            {objectItems.map((item, index) => {
              const image = String(item[imageKey] ?? "");
              return image ? (
                <img alt="" key={`${image}-${index}`} src={image} />
              ) : (
                <span key={`empty-${index}`}>0{index + 1}</span>
              );
            })}
          </div>
          <small>
            Так галерея будет выглядеть в кейсе. Нажмите на элемент ниже, чтобы
            заменить изображение.
          </small>
        </div>
      ) : null}
      {objectItems.map((item, index) => (
        <div className="visual-list-field__item" key={index}>
          <div className="visual-list-field__item-head">
            <span>
              {String(
                item.title ??
                  item.question ??
                  item.label ??
                  `Элемент ${index + 1}`,
              )}
            </span>
            <div className="mini-actions">
              <button
                disabled={index === 0}
                onClick={() => move(index, -1)}
                type="button"
              >
                <ArrowUpIcon size={14} />
              </button>
              <button
                disabled={index === objectItems.length - 1}
                onClick={() => move(index, 1)}
                type="button"
              >
                <ArrowDownIcon size={14} />
              </button>
              <button
                onClick={() => onChange([...objectItems, { ...item }])}
                type="button"
              >
                <PlusIcon size={14} />
              </button>
              <button
                onClick={() =>
                  onChange(
                    objectItems.filter((_, itemIndex) => itemIndex !== index),
                  )
                }
                type="button"
              >
                <TrashIcon size={14} />
              </button>
            </div>
          </div>
          {keys.map((key) =>
            isImageKey(key) ? (
              <MediaField
                key={key}
                label={fieldLabel(key)}
                media={media}
                onChange={(next) => update(index, key, next)}
                value={String(item[key] ?? "")}
              />
            ) : isLongTextKey(key) ? (
              <TextAreaField
                helper={textLengthHelper(String(item[key] ?? ""))}
                key={key}
                label={fieldLabel(key)}
                onChange={(event) => update(index, key, event.target.value)}
                rows={key === "body" || key === "answer" ? 5 : 3}
                value={String(item[key] ?? "")}
              />
            ) : (
              <TextField
                helper={textLengthHelper(String(item[key] ?? ""))}
                key={key}
                label={fieldLabel(key)}
                onChange={(event) => update(index, key, event.target.value)}
                value={String(item[key] ?? "")}
              />
            ),
          )}
        </div>
      ))}
      <Button
        onClick={() => onChange([...objectItems, emptyItem])}
        tone="secondary"
        type="button"
      >
        <PlusIcon size={15} /> Добавить элемент
      </Button>
    </fieldset>
  );
}

function CreateLocalization({
  pageId,
  available,
  onCreated,
}: {
  pageId: string;
  available: Locale[];
  onCreated: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [localeCode, setLocaleCode] = useState(available[0]?.code ?? "");
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (!available.some((item) => item.code === localeCode))
      setLocaleCode(available[0]?.code ?? "");
  }, [available, localeCode]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await apiRequest(`/api/v1/admin/pages/${pageId}/localizations`, {
        method: "POST",
        body: JSON.stringify({ locale_code: localeCode, title, slug }),
      });
      await onCreated();
      setOpen(false);
      setTitle("");
      setSlug("");
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось добавить перевод",
      );
    }
  }
  if (!available.length) return null;
  if (!open)
    return (
      <Button
        className="add-language"
        onClick={() => setOpen(true)}
        tone="quiet"
      >
        <PlusIcon size={16} /> Добавить перевод
      </Button>
    );
  return (
    <form className="localization-form" onSubmit={submit}>
      <SelectField
        label="Язык"
        onChange={(e) => setLocaleCode(e.target.value)}
        value={localeCode}
      >
        {available.map((locale) => (
          <option key={locale.id} value={locale.code}>
            {locale.label}
          </option>
        ))}
      </SelectField>
      <TextField
        label="Заголовок"
        onChange={(e) => setTitle(e.target.value)}
        required
        value={title}
      />
      <TextField
        error={error}
        label="Slug"
        onChange={(e) => setSlug(e.target.value)}
        required
        value={slug}
      />
      <div>
        <Button onClick={() => setOpen(false)} tone="quiet" type="button">
          Отмена
        </Button>
        <Button type="submit">Добавить</Button>
      </div>
    </form>
  );
}
